import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  Separator,
} from '@enechain/ecloud-designsystem'

import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { Badge } from '~/components/common/Badge'
import { GENERATION_DETAIL_SERIES_COLORS } from '~/constants/chartPalette'
// design hatsu-detail.html の行ラベル (`.form-label`) は md (14px) / 600 (2026-08-06 実測)。
import { typography } from '~/constants/typography'
import { convertAreaLabel } from '~/models/area'
import type {
  BgConfig,
  GenDetailSeriesVisibility,
} from '~/pages/BalanceGenerationDetail/constants'
import { TYPE_I18N_KEYS } from '~/pages/BalanceGenerationDetail/constants'

/** 基本表示トグルで切替可能な系列 (発電形式トグルは #2807 で追加予定)。 */
type BasicSeriesKey = 'genPlan' | 'genActual'

// 色は [[GENERATION_DETAIL_SERIES_COLORS]] から引く (= DetailChart の描画色と同じ出どころ)。
// design `.switch-item[data-color]` は switch の ON 色をチャート凡例色に揃える作法。
const BASIC_TOGGLES: Array<{
  key: BasicSeriesKey
  labelKey: string
  color: string
}> = [
  {
    key: 'genPlan',
    labelKey: 'page:balanceGenerationDetail.filter.genPlan',
    color: GENERATION_DETAIL_SERIES_COLORS.genPlan,
  },
  {
    key: 'genActual',
    labelKey: 'page:balanceGenerationDetail.filter.genActual',
    color: GENERATION_DETAIL_SERIES_COLORS.genActual,
  },
]

type Props = {
  displayDate: Date
  onDateChange: (date: Date) => void
  onToday: () => void
  bgConfig: BgConfig
  seriesVisibility: Pick<GenDetailSeriesVisibility, BasicSeriesKey>
  onToggleSeries: (key: BasicSeriesKey) => void
}

export const FilterCard = ({
  displayDate,
  onDateChange,
  onToday,
  bgConfig: bg,
  seriesVisibility,
  onToggleSeries,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <div
      style={{
        background: LightModeSemanticColors.Surface.primary.value,
        // design `css/balance.css:10` の `.filter-card { border:1px solid
        // var(--border-low) }`。 実装は 1 段濃い midEmphasis (#d9d9d9) で、
        // 4 画面とも同じズレ方をしていた (issue #7064)。
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '8px',
        padding: '12px',
        // design `css/balance.css:10` `.filter-card { margin-bottom:2px }`。
        // 詳細 2 画面だけ 4px に割れていた (#7064)。
        marginBottom: '2px',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <DateNav
          displayDate={displayDate}
          labelKeyPrefix="page:balanceGenerationDetail.filter"
          layout="horizontal"
          onDateChange={onDateChange}
          onToday={onToday}
        />

        <Separator height="24px" orientation="vertical" />

        <span style={typography.label}>
          {t('page:balanceGenerationDetail.filter.basicSeries')}
        </span>
        {BASIC_TOGGLES.map((item) => (
          <ToggleSwitch
            key={item.key}
            isOn={seriesVisibility[item.key]}
            label={t(item.labelKey)}
            onColor={item.color}
            onToggle={() => onToggleSeries(item.key)}
          />
        ))}

        <Separator height="24px" orientation="vertical" />

        <span style={typography.label}>BG</span>
        {/* BG のチップ群。 design hatsu-detail.html L76-79 はいずれも
            `.badge.badge-neutral` (= DS `Badge status="neutral"`) なので、
            raw span の独自 pill をやめて DS `Badge` に寄せる。 寸法・書体は
            `~/constants/system.ts` の badge recipe (14px / 400 / minH 24px / px 8px) 由来。

            並び順は design 実測 (コード → エリア → 地域 → 種別) に合わせる。
            [design 差異] design にある「地域」チップは dev に対応データが無く未実装
            (別 issue)。 調達種別チップは design に無い dev 独自の追加。 */}
        <Badge css={{ fontVariantNumeric: 'tabular-nums' }} status="neutral">
          {bg.code}
        </Badge>
        <Badge status="neutral">{convertAreaLabel(bg.areaCode)}</Badge>
        {/* procurementType は GetGenerationDetail 未露出 (#2000)。空チップを出さない */}
        {bg.procurementType !== '' && (
          <Badge status="neutral">{bg.procurementType}</Badge>
        )}
        <Badge status="neutral">{t(TYPE_I18N_KEYS[bg.type] ?? bg.type)}</Badge>
      </div>
    </div>
  )
}
